import styled from '@emotion/styled';
import React, { useEffect, useState } from 'react';
import TextField from '../../../components/forms/TextField';
import Button from '../../../components/general/Button';
import ModalTitle from '../../../components/general/ModalTitle';
import PageError from '../../../components/general/PageError';
import TooltipWrapper from '../../../components/general/TooltipWrapper';
import FooterBar from '../../../components/structure/FooterBar';
import HorizontalContainer from '../../../components/structure/HorizontalContainer';
import HorizontalSeparator from '../../../components/structure/HorizontalSeparator';
import ModalLayout from '../../../components/structure/ModalLayout';
import PageContainer from '../../../components/structure/PageContainer';
import { color_gradients_green_shift, color_shades_darkest, color_text_default } from '../../../constants/colors';
import LanguageModel from '../../../models/features/api/LanguageModel';
import EditableInteractionModel from '../../../models/interactionModel/EditableInteractionModel';
import TextAreaField from '../../../components/forms/TextAreaField';
import RadioGroup from '../../../components/forms/RadioGroup';
import { style_border_default } from '../../../constants/stylesValues';
import { css } from '@emotion/css';

const closeIcon = require('../../../content/images/close_icon.svg');
const copyIcon = require('../../../content/images/copy-text.svg');

export const enabledOptions = [
    { label: "LLM Intent Matching Enabled", value: true },
    { label: "LLM Intent Matching Disabled", value: false }
];

interface ManageIntentPromptModalProps {
    handleClose: () => void;
    handleSubmit: (enabled: boolean, prompt: string) => any;
    isLoading: boolean;
    languages?: LanguageModel[];
    prompt: string;
    enabled: boolean;

}

interface CopyVariableButtonProps {
    variable: string;
    copied: boolean;
    onCopy: (variable: string) => void;
}

const CopyVariableButton: React.FC<CopyVariableButtonProps> = ({ variable, copied, onCopy }) => (
    <TooltipWrapper text={copied ? "Copied!" : "Copy variable"} place="top">
        <VariableCopyButton
            type="button"
            title={`Copy ${variable}`}
            aria-label={`Copy ${variable}`}
            onClick={() => onCopy(variable)}
            copied={copied}>
            <img src={copyIcon} alt="" />
        </VariableCopyButton>
    </TooltipWrapper>
);

const ManageIntentPromptModal: React.FC<ManageIntentPromptModalProps> = (props) => {
    const [prompt, setPrompt] = useState(props.prompt);
    const [enabled, setEnabled] = useState(props.enabled);
    const [errorMessages, setErrorMessages] = useState([]);
    const [copiedVariable, setCopiedVariable] = useState<string | null>(null);

    useEffect(() => {
        if (!copiedVariable) {
            return;
        }

        const timeout = window.setTimeout(() => setCopiedVariable(null), 1500);
        return () => window.clearTimeout(timeout);
    }, [copiedVariable]);

    const handlePromptChange = value => {
        setErrorMessages([]);
        setPrompt(value);
    };

    const copyVariable = async (variable: string) => {
        try {
            await navigator.clipboard.writeText(variable);
        } catch {
            const textArea = document.createElement('textarea');
            textArea.value = variable;
            textArea.style.position = 'fixed';
            textArea.style.opacity = '0';
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
        }

        setCopiedVariable(variable);
    };
    
    return (
        <NewIntentModal 
            isVisible={true} 
            onClose={() => props.handleClose()} >        
            <PromptPageContainer fill withFooter>
                <HorizontalContainer>
                    <ModalTitle padded>Manage Intent Matching Prompt</ModalTitle>
                    <Button themes={['end-tight', 'icon']} 
                        icon={closeIcon} 
                        onClick={() => props.handleClose()} />
                </HorizontalContainer>
                <HorizontalSeparator />
                <ContentContainer>
                    <div className="main-body">
                        <CustomRadioGroup
                            checkContainerStyle={checkContainerStyle}
                            selectedOptionStyle={selectedOptionStyle}
                            onChange={(e) => setEnabled(e.value as boolean)}
                            value={enabled}
                            options={enabledOptions}
                        />
                        {
                            enabled && <>
                                <VariableDescriptions>
                                    <strong>Available variables</strong>
                                    <div>
                                        <code>{"{INTENTS}"}</code>
                                        <span>Will be replaced by a new line separated list of relevant intents in this format: <code>d: description. a:INTENT_NAME</code>.</span>
                                        <CopyVariableButton variable="{INTENTS}" copied={copiedVariable === "{INTENTS}"} onCopy={copyVariable} />
                                    </div>
                                    <div>
                                        <code>{"{LAST_ASSISTANT_SPEECH}"}</code>
                                        <span>Will be replaced by the last thing the assistant said. You can optionally include it if you want to prompt with more context.</span>
                                        <CopyVariableButton variable="{LAST_ASSISTANT_SPEECH}" copied={copiedVariable === "{LAST_ASSISTANT_SPEECH}"} onCopy={copyVariable} />
                                    </div>
                                    <div className="transcript-variable">
                                        <code>{"{LAST_TURNS_OF_TRANSCRIPT[N]}"}</code>
                                        <span>Will be replaced by the last N completed user and assistant exchanges, in chronological order. Replace N with the desired number. If fewer than N previous exchanges are available, all available exchanges will be included.</span>
                                        <CopyVariableButton variable="{LAST_TURNS_OF_TRANSCRIPT[N]}" copied={copiedVariable === "{LAST_TURNS_OF_TRANSCRIPT[N]}"} onCopy={copyVariable} />
                                        <details>
                                            <summary>Example output for <code>{"{LAST_TURNS_OF_TRANSCRIPT[3]}"}</code></summary>
                                            <pre>{`u: I need help with an existing order
a: Sure, what is your order number?
u: It is 12345
a: I found that order. What would you like to know?
u: When will it arrive?
a: It is expected to arrive tomorrow.`}</pre>
                                        </details>
                                        <p><strong>Note:</strong> <code>{"{LAST_ASSISTANT_SPEECH}"}</code> is normally redundant when using <code>{"{LAST_TURNS_OF_TRANSCRIPT[N]}"}</code>. Use both only to repeat the latest assistant response.</p>
                                    </div>
                                </VariableDescriptions>
                                <CustomTextArea
                                    required={true}
                                    name={`prompt`}
                                    value={prompt}
                                    maxRows={20}
                                    disabled={props.isLoading || !enabled}
                                    placeholder="Edit intent matching prompt here"
                                    onChange={(e) => handlePromptChange(e.target.value)}
                                    onBlur={() => { }} />                            
                            </>
                        }
                    </div>
                    <PageError errors={[
                        ...errorMessages ?? []
                        ]} onClear={() => {
                            setErrorMessages([]);
                        }} />
                </ContentContainer>
                <CustomFooter>
                    <SubmitButton
                        type="button" 
                        themes={['primary']} 
                        text="Save" 
                        onClick={() => props.handleSubmit(enabled, prompt)} 
                        disabled={props.isLoading || prompt.length == 0 } 
                        loading={props.isLoading} />
                    <Button
                        type="button"
                        themes={['secondary']}
                        text="Cancel"
                        onClick={() => props.handleClose()}
                        disabled={props.isLoading}
                        loading={props.isLoading} />
                </CustomFooter>
            </PromptPageContainer>
        </NewIntentModal>
    );

};

export default ManageIntentPromptModal;

const CustomTextArea = styled(TextAreaField)`
    margin-top: 0;
`;
const PromptPageContainer = styled(PageContainer)`
    box-sizing: border-box;
    min-height: 0;
`;
const ContentContainer = styled(HorizontalContainer)`
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    align-items: flex-start;
`;
const VariableDescriptions = styled.div`
    margin: 0 0 20px;

    > strong {
        display: block;
        margin-bottom: 6px;
    }

    > div {
        display: grid;
        grid-template-columns: max-content 1fr max-content;
        align-items: baseline;
        gap: 12px;
        margin: 6px 0;
        padding: 8px 10px;
        background: rgba(50, 70, 97, 0.04);
        border-radius: 4px;
        line-height: 1.4;
    }

    code {
        color: ${color_shades_darkest};
        font-family: inherit;
        font-weight: 600;
    }

    details {
        margin: 0;
    }

    summary {
        cursor: pointer;
        font-weight: 600;
    }

    pre {
        margin: 8px 0 0;
        padding: 10px 12px;
        white-space: pre-wrap;
        background: rgba(50, 70, 97, 0.04);
        border: ${style_border_default};
        border-radius: 4px;
        color: ${color_shades_darkest};
        font-family: inherit;
        font-size: inherit;
        line-height: 1.4;
    }

    p {
        margin: 8px 0 0;
        line-height: 1.4;
    }

    .transcript-variable {
        details,
        p {
            grid-column: 1 / -1;
        }

        p {
            margin-top: 0;
        }
    }
`;
const VariableCopyButton = styled.button<{ copied?: boolean }>`
    width: 24px;
    height: 24px;
    padding: 5px;
    display: flex;
    align-items: center;
    justify-content: center;
    align-self: start;
    border: none;
    border-radius: 4px;
    background: transparent;
    cursor: pointer;
    opacity: ${props => props.copied ? 1 : 0.65};

    &:hover,
    &:focus {
        background: rgba(50, 70, 97, 0.08);
        opacity: 1;
        outline: none;
    }

    img {
        width: 14px;
        height: 14px;
    }
`;
const NewIntentModal = styled(ModalLayout)`
    color: ${color_text_default};
    width: 70%;
    min-width: 600px;

    .horizontal-separator {
        flex-grow: 0;
    }

    .main-body {
        width: 100%;
        box-sizing: border-box;
        padding: 32px;
        padding-top: 8px;
    }
`;

const CustomFooter = styled(FooterBar)`
    z-index: auto;
`;

const SubmitButton = styled(Button)`
    margin-right: auto;
`;

const CustomRadioGroup = styled(RadioGroup)`
    margin: 0 0 26px 0;
`;

const checkContainerStyle = css`
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background-color: #ffffff;
    border: ${style_border_default};
    display: flex;
    align-items: center;
    justify-content: center;
`;
const selectedOptionStyle = css`
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-image: ${color_gradients_green_shift};
`;
